import React, { useState, useEffect } from 'react';
import { FileText, RefreshCw, Copy, Check, ChevronDown } from 'lucide-react';
import { getAuthHeaders } from '@/services/authConfig';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface LogViewerProps {
  trajectoryId: string;
}

export function LogViewer({ trajectoryId }: LogViewerProps) {
  const [expanded, setExpanded] = useState(false);
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('ALL');
  const [copied, setCopied] = useState(false);
  const [logSource, setLogSource] = useState<'s3' | 'events' | null>(null);

  const fetchLogs = async () => {
    if (!trajectoryId) return;

    setLoading(true);
    setError(null);

    try {
      // First try S3 run logs (actual Python logging output)
      const s3Response = await fetch(`/api/trajectories/${trajectoryId}/run-logs`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const s3Data = await s3Response.json();

      if (s3Data.log_content && !s3Data.error) {
        setLogs(s3Data.log_content);
        setLogSource('s3');
        return;
      }

      // Fall back to event-based logs
      const levelParam = filter !== 'ALL' ? `&level=${filter}` : '';
      const response = await fetch(`/api/trajectories/${trajectoryId}/logs?${levelParam}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data = await response.json();

      if (data.log_content) {
        setLogs(data.log_content);
        setLogSource('events');
      } else if (data.status === 'not_found') {
        setLogs('');
        setError('No log file available for this trajectory.');
      } else {
        setError(data.message || 'Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to fetch logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch logs when expanded or filter changes
  useEffect(() => {
    if (expanded) {
      fetchLogs();
    }
  }, [expanded, trajectoryId, filter]);

  const handleCopy = () => {
    navigator.clipboard.writeText(logs).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  // Apply syntax highlighting to log lines
  const highlightLogs = (logContent: string) => {
    if (!logContent) return null;

    return logContent.split('\n').map((line, index) => {
      let colorClass = 'text-gray-300';

      if (line.includes('[ERROR]')) {
        colorClass = 'text-red-400';
      } else if (line.includes('[WARNING]')) {
        colorClass = 'text-yellow-400';
      } else if (line.includes('[DEBUG]')) {
        colorClass = 'text-gray-500';
      } else if (line.includes('[INFO]')) {
        colorClass = 'text-blue-400';
      }

      return (
        <div key={index} className={cn('leading-relaxed', colorClass)}>
          {line}
        </div>
      );
    });
  };

  return (
    <Card className="mb-4">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <span className="font-semibold">Run Logs</span>
              {logSource && (
                <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                  {logSource === 's3' ? 'S3' : 'Events'}
                </span>
              )}
            </div>
            <ChevronDown className={cn(
              "h-4 w-4 transition-transform",
              expanded && "rotate-180"
            )} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 mb-4">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Levels</SelectItem>
                  <SelectItem value="ERROR">Errors Only</SelectItem>
                  <SelectItem value="WARNING">Warnings+</SelectItem>
                  <SelectItem value="INFO">Info+</SelectItem>
                  <SelectItem value="DEBUG">Debug</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopy}
                disabled={!logs}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="text-sm text-muted-foreground py-4">
                {error}
              </div>
            ) : logs ? (
              <div className="bg-[#1e1e1e] rounded-lg p-4 max-h-[500px] overflow-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                  {highlightLogs(logs)}
                </pre>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4">
                No logs available for this trajectory.
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default LogViewer;
